import { useEffect, useState } from 'react';
import { InputNumber } from 'antd';
import * as Yup from 'yup';
import { Modal } from 'src/components/baseComponents';
import { toast } from '@insight-health-ai/roseate/components/toast';
import { CommonFormProps } from '.';
import { analytics } from 'src/services';
import { getAppointmentDetailsForAnalytics } from 'src/utils';

type Props = CommonFormProps;

export const BANG = ({ isOpen, appointment, form, onSave, loading }: Props) => {
  const [initialValues, setInitialValues] = useState({});
  const [fields, setFields] = useState<any[]>([]);
  const [validationSchema, setValidationSchema] = useState<
    Yup.ObjectSchema<any>
  >(Yup.object());
  const [height, setHeight] = useState<number>(0);
  const [weight, setWeight] = useState<number>(0);
  const [bmi, setBMI] = useState(0);

  const bangform = form.content as any[];

  useEffect(() => {
    const updatedValidationSchema = bangform?.reduce((schema, item) => {
      if (item.optional === 'false') {
        const key = item.item.toLowerCase().replace(/\s+/g, '_');
        const validationRule =
          item.type === 'checkbox'
            ? Yup.bool().oneOf([true], `${item.item} must be checked`)
            : Yup.string().required(`${item.item} is required`);

        return schema.shape({
          [key]: validationRule,
        });
      }

      return schema;
    }, Yup.object());
    setValidationSchema(updatedValidationSchema);
  }, []);

  useEffect(() => {
    const updatedValuesArray = bangform?.map((curr) => {
      const key = curr.item.toLowerCase().replace(/\s+/g, '_');
      return { [key]: '' };
    });

    const updatedValues = Object.assign({}, ...updatedValuesArray);
    setInitialValues((prevValues) => ({ ...prevValues, ...updatedValues }));
  }, []);

  useEffect(() => {
    const transformedArray = bangform.map((item) => {
      if (item.type !== 'header' && item.type !== 'label') {
        const transformedItem = {
          name: item.item.toLowerCase().replace(/\s+/g, '_'),
          type: item.type,
          title: item.item,
          placeholder: item.placeholder ? item.placeholder : '',
          autoComplete: 'off',
        };
        Object.assign(transformedItem, { options: item.options });
        return transformedItem;
      }
      return {
        name: item.item,
        type: item.type,
        title: item.item,
      };
    });
    setFields(transformedArray);
  }, []);

  useEffect(() => {
    const weightInKg = weight * 0.453592;
    const heightInMeters = height / 39.37;
    const heightSquare = heightInMeters * heightInMeters;
    const firstCal = weightInKg / heightSquare;
    const newBMI = firstCal;
    setBMI(newBMI);
  }, [height, weight]);

  const calculateScore = (values: Record<string, any>) => {
    let score = 0;
    for (const key in values) {
      const value = values[key];
      if (
        value === 'Yes' ||
        value === '>50 years' ||
        value === '>40 cm' ||
        value === 'Male' ||
        bmi > 35
      ) {
        score++;
      }
    }

    return score;
  };

  const handleSubmit = async (values: any) => {
    analytics.track(
      'Clicked Submit In Stop-Bang Score Form',
      getAppointmentDetailsForAnalytics(appointment!)
    );

    if (!values) {
      return;
    }
    const filteredValues = filterValues(values);
    if (Object.keys(filteredValues).length !== bangform.length) {
      toast.error('Please fill all the fields before proceeding');
      return;
    }
    const score = calculateScore(filteredValues);
    const payload = {
      response: {
        user_response: filteredValues,
        STOP_BANG_Score: score,
      },
      form_type: 'Obstructive_Sleep_Apnea',
    };
    await onSave(payload);
  };

  const filterValues = (values: Record<string, any>) => {
    const filtered: Record<string, any> = {};
    for (const [key, value] of Object.entries(values)) {
      if (value !== false && value !== '') {
        filtered[key] = value;
      }
    }
    filtered['BMI_in_kg/m2'] = bmi?.toFixed(2);
    return filtered;
  };

  return (
    <div>
      {/** @ts-ignore */}
      <Modal
        open={isOpen}
        className={'review-of-system-modal'}
        formClassName={'grid grid-cols-2 gap-4'}
        initialValues={initialValues}
        validationSchema={validationSchema}
        submitButtonText="Submit"
        cancelButtonText=""
        closable={false}
        centered={true}
        fields={fields}
        onSubmitLoading={loading}
        disableSubmit={loading}
        handleSubmit={handleSubmit}
        bangform={true}
        additionalBody={
          <div
            style={{
              display: 'inline-flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '18px',
            }}
          >
            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
              <div style={{ flex: 1 }}>
                <label className="text-semibold font-bold" htmlFor="height">
                  Height (in inches):
                </label>
                <InputNumber
                  id="height"
                  min={0}
                  value={height}
                  onChange={(value) => setHeight(value as number)}
                  style={{
                    width: '100%',
                    borderRadius: '5px',
                    padding: '6px',
                    border: '1px solid #00D090',
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label className="text-semibold font-bold" htmlFor="weight">
                  Weight (in lbs):
                </label>
                <InputNumber
                  id="weight"
                  value={weight}
                  min={0}
                  onChange={(value) => setWeight(value as number)}
                  style={{
                    width: '100%',
                    borderRadius: '5px',
                    padding: '6px',
                    border: '1px solid #00D090',
                  }}
                />
              </div>
              <div className="text-semibold font-bold mt-8 ml-6">
                BMI: {bmi ? bmi.toFixed(2) : '0.00'}
              </div>
            </div>
          </div>
        }
      />
    </div>
  );
};
